<template>
  <div class="multi-select-search">
    <v-container class="__selected-container">
      <div v-if="selections.length === 0" class="__empty">
        {{ emptySelectionsLabel }}
      </div>
      <v-row>
        <v-col class="__col" v-for="(selection, i) in selections" :key="i" cols="auto">
          <BaseChip
            size="sm"
            preset="soft"
            tone="info"
            icon="check-circle"
            clearable
            :clear-label="$t('actions.delete')"
            :interactive="false"
            @clear="removeFromSelection(i)"
          >
            {{ selection }}
          </BaseChip>
        </v-col>
      </v-row>
    </v-container>
    <div class="__custom-input-container">
      <BaseSearchBar
        name="name"
        ref="customSearchBar"
        :placeholder="$t('page.business-intelligence.normalized-names.search')"
        hasDebounce
        isGrey
        @input="onSearch"
        :disabled="initialLoading"
      ></BaseSearchBar>
    </div>
    <BaseLoadingBar v-if="loading" />
    <div class="__description">{{ descriptionLabel }}</div>
    <v-list v-if="hasSelections" class="__suggestion-container">
      <!-- custom normalized name -->
      <v-list-item
        v-if="allowAddCustom && !loading && search.length > 0 && !selections.includes(search)"
        key="custom"
        :disabled="loading"
        @click="addCustomInputToSelection()"
      >
        <v-list-item-title class="__item __custom"
          ><BaseIcon name="plus" />{{
            $t('page.business-intelligence.normalized-names.add-custom', { normalizedName: search })
          }}</v-list-item-title
        >
      </v-list-item>
      <div v-if="!allowAddCustom && suggestions.length === 0" class="__empty">
        {{ emptySuggestionsLabel }}
      </div>
      <template v-for="suggestion in suggestions">
        <v-list-item
          v-if="!selections.includes(suggestion) && suggestion !== search"
          :key="suggestion"
          :disabled="loading"
          @click="addToSelection(suggestion)"
        >
          <v-list-item-title class="__item"><BaseIcon name="plus" />{{ suggestion }}</v-list-item-title>
        </v-list-item>
      </template>
    </v-list>
    <div v-else class="__empty">
      {{ emptySuggestionsLabel }}
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import BaseChip from '@/components/base/BaseChip.vue';
import BaseLoadingBar from '@/components/base/BaseLoadingBar.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import BaseSearchBar from '@/components/base/BaseSearchBarNew.vue';

export default {
  name: 'BaseMultiSelectSearch',
  components: {
    BaseChip,
    BaseLoadingBar,
    BaseIcon,
    BaseSearchBar,
  },
  computed: {
    ...mapGetters('tenant', ['tenant']),
    hasSelections() {
      const hasSuggestionsLeft = this.suggestions.filter((suggestion) => !this.selections.includes(suggestion));
      return hasSuggestionsLeft.length > 0 || (this.search.length > 0 && !this.selections.includes(this.search));
    },
  },
  data() {
    return {
      normalizedName: '',
      search: '',
      selections: [...this.existingSelections],
    };
  },
  props: {
    suggestions: {
      type: Array,
      required: true,
      default: () => [],
    },
    existingSelections: {
      type: Array,
      required: false,
      default: () => [],
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    initialLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowAddCustom: {
      type: Boolean,
      default: true,
    },
    emptySelectionsLabel: {
      type: String,
      default: '',
    },
    emptySuggestionsLabel: {
      type: String,
      default: '',
    },
    descriptionLabel: {
      type: String,
      default: '',
    },
  },
  watch: {
    existingSelections() {
      this.selections = [...this.existingSelections];
    },
  },
  methods: {
    reset() {
      this.selections = [];
      this.normalizedName = '';
      this.search = '';
      this.$refs.customSearchBar.onClear();
    },
    addToSelection(value) {
      if (value.length > 0) {
        this.selections.push(value);
        this.$emit('updated-selected-names', this.selections);
      }
      if (!this.hasSelections) {
        this.$refs.customSearchBar.onClear();
        this.search = '';
        this.$emit('all-names-selected');
      }
    },
    addCustomInputToSelection() {
      this.addToSelection(this.search);

      this.$refs.customSearchBar.onClear();
      this.search = '';
    },
    removeFromSelection(index) {
      this.selections.splice(index, 1);

      this.$emit('updated-selected-names', this.selections);
    },
    onSearch(query) {
      this.search = query;
      this.$emit('on-search', query);
    },
  },
};
</script>

<style lang="scss">
.multi-select-search {
  > .__custom-input-container {
    display: flex;
    align-items: center;
    gap: 4px;

    .form-input__input {
      min-height: 32px;
    }
  }

  > .__selected-container {
    padding: 0;
    margin-bottom: 12px;

    .__col {
      padding: 4px;
    }
  }

  > .__description {
    font-size: $font-size-small;
    color: $color-normal-pale;
    padding: 12px 0;
  }

  > .__suggestion-container {
    max-height: 40vh;
    overflow-y: scroll;

    .__item {
      display: flex;
      align-items: center;

      &.__custom {
        text-decoration: underline;
      }

      .base-icon {
        width: 18px;
        margin-right: 8px;
      }
    }
  }

  .__empty {
    display: flex;
    font-size: $font-size-root;
    color: $color-neutral;
    margin: 12px 0;
  }
}
</style>
